"use client";

import { useState } from "react";
import Link from "next/link";
import {
  UserPlus,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle2,
  User,
  Building2,
} from "lucide-react";
import { getSupabaseBrowser } from "@/lib/supabase/browser";
import { BrandLogo } from "@/components/BrandLogo";
import { toErrorMessage, isTransientServerError } from "@/lib/errors";

type CustomerType = "private" | "business";

// Branchen-Vorschläge im Dropdown – „Sonstiges" am Ende für alles, was
// nicht passt. Pflegen wir hier statisch, weil das Set selten wechselt.
const INDUSTRIES = [
  "Handwerk",
  "Gastronomie / Hotellerie",
  "Einzelhandel / E-Commerce",
  "Industrie / Produktion",
  "IT / Software",
  "Beratung / Dienstleistung",
  "Gesundheit / Medizin",
  "Bildung / Coaching",
  "Bau / Immobilien",
  "Marketing / Agentur",
  "Verein / Non-Profit",
  "Sonstiges",
];

const EMPLOYEE_BANDS = [
  "1 (Solo-Selbstständig)",
  "2 – 5",
  "6 – 20",
  "21 – 50",
  "51 – 200",
  "200+",
];

export default function RegistrierungPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Privat vs. Geschäft. Default „private" für die niedrigste Hürde.
  const [customerType, setCustomerType] = useState<CustomerType>("private");
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [vatId, setVatId] = useState("");
  const [employees, setEmployees] = useState("");
  // Newsletter: OPTIONAL und standardmäßig AUS. Eine vorangekreuzte Box oder
  // ein automatischer Eintrag wäre keine wirksame Einwilligung (Art. 7 DSGVO,
  // EuGH „Planet49"; § 7 Abs. 2 Nr. 2 UWG). Bei Häkchen → Double-Opt-In.
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (customerType === "business" && companyName.trim().length < 2) {
      setError("Bitte den Firmennamen angeben.");
      return;
    }

    setLoading(true);
    try {
      const supabase = getSupabaseBrowser();
      const { error: authError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
            customer_type: customerType,
            // Firmen-Felder nur bei Geschäftskunden mitsenden – sonst
            // bleibt user_metadata sauber.
            ...(customerType === "business" && {
              company: companyName.trim(),
              industry: industry || null,
              vat_id: vatId.trim() || null,
              employees: employees || null,
            }),
          },
        },
      });

      if (authError) {
        // .message kann bei Server-Störungen leer sein → defensiv absichern.
        const lower = (authError.message ?? "").toLowerCase();
        if (isTransientServerError(authError)) {
          setError(
            "Die Registrierung ist gerade kurz nicht erreichbar. Bitte versuchen Sie es in einem Moment noch einmal.",
          );
        } else {
          setError(
            lower.includes("already registered")
              ? "Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich an."
              : toErrorMessage(authError, "Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut."),
          );
        }
        return;
      }

      // Newsletter NUR bei gesetztem Opt-in – und dann über den regulären
      // Double-Opt-In (POST /api/newsletter): Erst der Klick auf die
      // Bestätigungs-Mail aktiviert das Abo. Best-effort, darf die
      // Registrierung weder blockieren noch scheitern lassen.
      if (newsletterOptIn) {
        void fetch("/api/newsletter", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        }).catch(() => null);
      }

      setSuccess(true);
    } catch (err) {
      setError(
        isTransientServerError(err)
          ? "Die Registrierung ist gerade kurz nicht erreichbar. Bitte versuchen Sie es in einem Moment noch einmal."
          : toErrorMessage(err, "Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut."),
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="flex min-h-[70vh] items-center py-16">
        <div className="mx-auto w-full max-w-md px-4">
          <div className="mb-6 flex justify-center">
            <BrandLogo />
          </div>
          <div
            className="rounded-xl border p-8 text-center"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--card-bg)",
            }}
          >
            <div
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
              style={{
                backgroundColor: "var(--fi-success-bg)",
                color: "var(--fi-success-fg)",
              }}
            >
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h1 className="mt-4 text-xl font-bold" style={{ color: "var(--text)" }}>
              Konto erstellt
            </h1>
            <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
              Wir haben Ihnen eine Bestätigungs-E-Mail an{" "}
              <strong style={{ color: "var(--text)" }}>{email}</strong> gesendet.
              Bitte klicken Sie den Link in der Mail, um Ihr Konto zu aktivieren.
            </p>
            {newsletterOptIn && (
              <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
                Außerdem haben wir Ihnen eine{" "}
                <strong style={{ color: "var(--text)" }}>separate E-Mail</strong>{" "}
                zur Bestätigung Ihrer Newsletter-Anmeldung geschickt – das Abo
                wird erst nach diesem Klick aktiv.
              </p>
            )}
            <p className="mt-3 text-xs" style={{ color: "var(--text-tertiary)" }}>
              Keine Mail erhalten? Bitte prüfen Sie auch den Spam-Ordner.
            </p>
            <Link
              href="/login"
              className="mt-6 inline-block text-sm font-semibold text-brand-500 hover:underline"
            >
              Zum Login
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex min-h-[70vh] items-center py-16">
      <div className="mx-auto w-full max-w-md px-4">
        <div className="text-center">
          <div className="flex justify-center">
            <BrandLogo priority />
          </div>
          <h1 className="mt-6 text-2xl font-bold" style={{ color: "var(--text)" }}>
            Konto erstellen
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            Erstellen Sie ein Konto, um das Dashboard und das Ticket-System zu nutzen.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-8 space-y-4 rounded-xl border p-6"
          style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
        >
          {/* Privat vs. Geschäft Toggle */}
          <div>
            <p
              className="mb-2 text-xs font-semibold uppercase tracking-wider"
              style={{ color: "var(--text-tertiary)" }}
            >
              Konto-Art
            </p>
            <div
              className="grid grid-cols-2 gap-1 rounded-lg border p-1"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--bg)",
              }}
            >
              {(
                [
                  { id: "private", label: "Privatkunde", Icon: User },
                  { id: "business", label: "Geschäftskunde", Icon: Building2 },
                ] as { id: CustomerType; label: string; Icon: typeof User }[]
              ).map((opt) => {
                const active = customerType === opt.id;
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCustomerType(opt.id)}
                    className="flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors"
                    style={
                      active
                        ? {
                            backgroundColor: "var(--color-brand-500)",
                            color: "white",
                          }
                        : { color: "var(--text-secondary)" }
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="reg-firstname" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                Vorname *
              </label>
              <input
                type="text"
                id="reg-firstname"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                autoComplete="given-name"
                className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
                style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
              />
            </div>
            <div>
              <label htmlFor="reg-lastname" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                Nachname *
              </label>
              <input
                type="text"
                id="reg-lastname"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                autoComplete="family-name"
                className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
                style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
              />
            </div>
          </div>

          <div>
            <label htmlFor="reg-email" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
              E-Mail *
            </label>
            <input
              type="email"
              id="reg-email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete="email"
              className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
              style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
            />
          </div>

          {/* Firmen-Felder – nur sichtbar bei Geschäftskunden */}
          {customerType === "business" && (
            <div
              className="space-y-4 rounded-lg border p-4"
              style={{
                borderColor: "var(--fi-brand-fg)",
                backgroundColor: "var(--fi-brand-bg)",
              }}
            >
              <p
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider"
                style={{ color: "var(--fi-brand-fg)" }}
              >
                <Building2 className="h-3.5 w-3.5" />
                Firmen-Informationen
              </p>
              <div>
                <label
                  htmlFor="reg-company"
                  className="mb-1.5 block text-sm font-medium"
                  style={{ color: "var(--text)" }}
                >
                  Firmenname *
                </label>
                <input
                  type="text"
                  id="reg-company"
                  required={customerType === "business"}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={loading}
                  autoComplete="organization"
                  placeholder="Mustermann GmbH"
                  className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
                  style={{
                    backgroundColor: "var(--bg)",
                    borderColor: "var(--border)",
                    color: "var(--text)",
                  }}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="reg-industry"
                    className="mb-1.5 block text-sm font-medium"
                    style={{ color: "var(--text)" }}
                  >
                    Branche
                  </label>
                  <select
                    id="reg-industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    disabled={loading}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
                    style={{
                      backgroundColor: "var(--bg)",
                      borderColor: "var(--border)",
                      color: "var(--text)",
                    }}
                  >
                    <option value="">Bitte auswählen …</option>
                    {INDUSTRIES.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="reg-employees"
                    className="mb-1.5 block text-sm font-medium"
                    style={{ color: "var(--text)" }}
                  >
                    Mitarbeiter
                  </label>
                  <select
                    id="reg-employees"
                    value={employees}
                    onChange={(e) => setEmployees(e.target.value)}
                    disabled={loading}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
                    style={{
                      backgroundColor: "var(--bg)",
                      borderColor: "var(--border)",
                      color: "var(--text)",
                    }}
                  >
                    <option value="">Bitte auswählen …</option>
                    {EMPLOYEE_BANDS.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label
                  htmlFor="reg-vat"
                  className="mb-1.5 block text-sm font-medium"
                  style={{ color: "var(--text)" }}
                >
                  USt-IdNr. <span style={{ color: "var(--text-tertiary)" }}>(optional)</span>
                </label>
                <input
                  type="text"
                  id="reg-vat"
                  value={vatId}
                  onChange={(e) => setVatId(e.target.value)}
                  disabled={loading}
                  placeholder="DE123456789"
                  className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
                  style={{
                    backgroundColor: "var(--bg)",
                    borderColor: "var(--border)",
                    color: "var(--text)",
                  }}
                />
              </div>
              <p
                className="text-[11px] leading-snug"
                style={{ color: "var(--fi-brand-fg)", opacity: 0.85 }}
              >
                Diese Angaben helfen uns, Angebote und Rechnungen direkt
                korrekt auszustellen. Alles kann später im Profil angepasst werden.
              </p>
            </div>
          )}

          <div>
            <label htmlFor="reg-password" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
              Passwort *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="reg-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="new-password"
                className="w-full rounded-lg border px-4 py-2.5 pr-10 text-sm outline-none focus:border-brand-500 disabled:opacity-50"
                style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" style={{ color: "var(--text-secondary)" }} />
                ) : (
                  <Eye className="h-4 w-4" style={{ color: "var(--text-secondary)" }} />
                )}
              </button>
            </div>
            <p className="mt-1 text-xs" style={{ color: "var(--text-secondary)" }}>
              Mindestens 8 Zeichen
            </p>
          </div>

          <label className="flex items-start gap-3">
            <input type="checkbox" required className="mt-1 h-4 w-4 rounded accent-brand-500" />
            <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Ich habe die{" "}
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-500">
                Datenschutzerklärung
              </a>{" "}
              gelesen und stimme der Verarbeitung meiner Daten zu. *
            </span>
          </label>

          <label className="flex items-start gap-3">
            <input type="checkbox" required className="mt-1 h-4 w-4 rounded accent-brand-500" />
            <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Ich akzeptiere die{" "}
              <a href="/agb" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-500">
                Allgemeinen Geschäftsbedingungen (AGB)
              </a>
              . *
            </span>
          </label>

          {/* Newsletter-Opt-in: freiwillig, getrennt von den Pflicht-Häkchen,
              NICHT vorangekreuzt, informiert (was / wie oft / widerrufbar). */}
          <label
            className="flex items-start gap-3 rounded-lg border px-3 py-2.5"
            style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)" }}
          >
            <input
              type="checkbox"
              checked={newsletterOptIn}
              onChange={(e) => setNewsletterOptIn(e.target.checked)}
              disabled={loading}
              className="mt-1 h-4 w-4 rounded accent-brand-500"
            />
            <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              <span className="font-medium" style={{ color: "var(--text)" }}>
                Newsletter erhalten (optional)
              </span>
              <br />
              Ja, ich möchte den DMD Studio Newsletter mit Tipps zu Webdesign,
              SEO und Online-Marketing per E-Mail erhalten (ca. 1–2× im Monat).
              Die Anmeldung wird per Bestätigungs-Mail abgesichert; abmelden
              kann ich jederzeit mit einem Klick.
            </span>
          </label>

          {error && (
            <div
              className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm"
              style={{
                backgroundColor: "var(--fi-error-bg)",
                borderColor: "var(--border)",
                color: "var(--fi-error-fg)",
              }}
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
          >
            <UserPlus className="h-4 w-4" />
            {loading ? "Wird erstellt …" : "Konto erstellen"}
          </button>
        </form>

        <p className="mt-4 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
          Bereits ein Konto?{" "}
          <Link href="/login" className="font-medium text-brand-500 hover:underline">
            Jetzt anmelden
          </Link>
        </p>
      </div>
    </section>
  );
}
